import {
  BrainCategory,
  BrainClassification,
  BrainEntityType,
  BrainEventActor,
  BrainEventKind,
  BrainEventOrigin,
  BrainKnowledgeState,
  BrainPredicate,
  BrainPublicationState,
  BrainReviewState,
  BrainSourceType,
} from './brain';
import { BrainFlagMode } from './runtimeFlags';

export enum BrainSyntheticEventKind {
  DEADLINE_DUE_SOON = 'DEADLINE_DUE_SOON',
  DEADLINE_PASSED = 'DEADLINE_PASSED',
  SIGNAL_DECAY_TICK = 'SIGNAL_DECAY_TICK',
  SIGNAL_REEVALUATE = 'SIGNAL_REEVALUATE',
}

export type BrainEventKindAny = BrainEventKind | BrainSyntheticEventKind;

export const BrainEventKindAny = Object.freeze({
  ...BrainEventKind,
  ...BrainSyntheticEventKind,
});

/** Durable event-log row. Ids, enums and dates only, so every field is readable. */
export interface BrainEvent {
  /** `${assertionId}#${revision}` | `${entityId}#${seq}` | `SWEEP#${kind}#${id}#${day}`; the team is the partition key, not part of the id. */
  eventId: string;
  teamId: string;
  kind: BrainEventKindAny;
  origin: BrainEventOrigin;
  actor: BrainEventActor;
  at: string;
  subjectEntityId?: string;
  /** Subject plus its partOf chain, root last. */
  scopeEntityIds: string[];
  category?: BrainCategory;
  predicate?: BrainPredicate;
  assertionId?: string;
  revision?: number;
  entityId?: string;
  causeAssertionId?: string;
  areaKeys: string[];
  spaceId?: string;
  classification?: BrainClassification;
  knowledgeState?: BrainKnowledgeState;
  reviewState?: BrainReviewState;
  publicationState?: BrainPublicationState;
  dueAt?: string;
  /** Source types of the fact's evidence refs; absent on entity and sweep events, which carry no source. */
  sourceTypes?: BrainSourceType[];
}

/** The BRAIN_SIGNALS scope's CONFIG row body; same shape and fail-closed rule as BrainFlagConfig. */
export interface BrainSignalsFlagConfig {
  mode: BrainFlagMode;
  teamIds: string[];
}

export const BRAIN_EVENT_TTL_DAYS = 90;

export enum BrainSignalLevel {
  NONE = 'NONE',
  LOW = 'LOW',
  MODERATE = 'MODERATE',
  HIGH = 'HIGH',
  CRITICAL = 'CRITICAL',
}

export const BRAIN_SIGNAL_LEVEL_ORDER: readonly BrainSignalLevel[] = [
  BrainSignalLevel.NONE,
  BrainSignalLevel.LOW,
  BrainSignalLevel.MODERATE,
  BrainSignalLevel.HIGH,
  BrainSignalLevel.CRITICAL,
];

export enum BrainSignalStatus {
  OPEN = 'OPEN',
  CLOSED = 'CLOSED',
}

export enum BrainSignalDefinitionStatus {
  ACTIVE = 'ACTIVE',
  PAUSED = 'PAUSED',
  ARCHIVED = 'ARCHIVED',
}

export enum BrainSignalScopeType {
  ENTITY = 'ENTITY',
}

export enum BrainSignalRuleKind {
  COUNT_IN_WINDOW = 'COUNT_IN_WINDOW',
  SUPERSEDED_COUNT = 'SUPERSEDED_COUNT',
  DEADLINE_PASSED_WITHOUT_SUCCESSOR = 'DEADLINE_PASSED_WITHOUT_SUCCESSOR',
  DEADLINE_MOVED = 'DEADLINE_MOVED',
  FACT_STATE_IS = 'FACT_STATE_IS',
  OWNER_CHANGED = 'OWNER_CHANGED',
  NO_NEW_EVIDENCE_FOR = 'NO_NEW_EVIDENCE_FOR',
}

export type BrainSignalRule =
  | {
      kind: BrainSignalRuleKind.COUNT_IN_WINDOW;
      category?: BrainCategory;
      predicate?: BrainPredicate;
      min: number;
      days: number;
      delta: number;
    }
  | {
      kind: BrainSignalRuleKind.SUPERSEDED_COUNT;
      predicate?: BrainPredicate;
      min: number;
      days: number;
      delta: number;
    }
  | {
      kind: BrainSignalRuleKind.DEADLINE_PASSED_WITHOUT_SUCCESSOR;
      predicate?: BrainPredicate;
      delta: number;
    }
  | {
      kind: BrainSignalRuleKind.DEADLINE_MOVED;
      predicate?: BrainPredicate;
      min: number;
      days: number;
      delta: number;
    }
  | {
      kind: BrainSignalRuleKind.FACT_STATE_IS;
      knowledgeState: BrainKnowledgeState;
      category?: BrainCategory;
      predicate?: BrainPredicate;
      delta: number;
    }
  | { kind: BrainSignalRuleKind.OWNER_CHANGED; days: number; delta: number }
  | {
      kind: BrainSignalRuleKind.NO_NEW_EVIDENCE_FOR;
      days: number;
      delta: number;
    };

export interface BrainSignalMatch {
  eventKinds?: BrainEventKindAny[];
  categories?: BrainCategory[];
  predicates?: BrainPredicate[];
  entityTypes: BrainEntityType[];
  entityIds?: string[];
  areaKeys?: string[];
  classifications?: BrainClassification[];
  sourceTypes?: BrainSourceType[];
}

export interface BrainSignalPolicy {
  cooldownHours: number;
  decayDays: number;
  closeOnResolution: boolean;
  reviewBeforeAction: boolean;
  maxEvaluationsPerDay: number;
  /** Interpreter verdicts below this confidence leave the level unchanged; defaults to BRAIN_SIGNAL_INTERPRETER.MIN_CONFIDENCE. */
  minConfidence?: number;
  /** Distinct sources a raise must cite; defaults to BRAIN_SIGNAL_INTERPRETER.MIN_DISTINCT_SOURCES_TO_RAISE. */
  minDistinctSources?: number;
}

export enum BrainSignalSemanticDepth {
  LIGHT = 'LIGHT',
  DEEP = 'DEEP',
}

export interface BrainSignalSemanticLevel {
  level: BrainSignalLevel;
  /** Sealed at rest, with the condition. */
  description: string;
}

export interface BrainSignalSemantic {
  /** Sealed at rest. */
  condition: string;
  levels: BrainSignalSemanticLevel[];
  depth: BrainSignalSemanticDepth;
}

export enum BrainSignalEscalationReason {
  HIGH_OR_CRITICAL = 'HIGH_OR_CRITICAL',
  TWO_LEVEL_JUMP = 'TWO_LEVEL_JUMP',
  DISPUTED_INPUT = 'DISPUTED_INPUT',
  CITATION_FAILURE = 'CITATION_FAILURE',
  DEPTH_DEEP = 'DEPTH_DEEP',
}

export const BRAIN_SIGNAL_INTERPRETER = {
  MAX_SCOPE_FACTS: 20,
  MAX_HISTORY: 5,
  MIN_CONFIDENCE: 0.6,
  PROMPT_VERSION: 'signal-v1',
  MAX_TOKENS: 600,
  MIN_DISTINCT_SOURCES_TO_RAISE: 2,
} as const;

export enum BrainSignalNotifyChannel {
  IN_APP = 'IN_APP',
  EMAIL = 'EMAIL',
}

export enum BrainSignalNotifyRole {
  ADMINS = 'ADMINS',
  REVIEWERS = 'REVIEWERS',
}

export interface BrainSignalActions {
  notify: {
    /** Sealed at rest. */
    userIds: string[];
    roles: BrainSignalNotifyRole[];
    channels: BrainSignalNotifyChannel[];
    minLevel: BrainSignalLevel;
  };
}

export interface BrainSignalScope {
  type: BrainSignalScopeType;
  entityTypes: BrainEntityType[];
}

export interface BrainSignalDefinitionVersion {
  defId: string;
  version: number;
  /** Sealed at rest. */
  name: string;
  /** Sealed at rest. */
  description?: string;
  lang: string;
  templateId?: string;
  scope: BrainSignalScope;
  match: BrainSignalMatch;
  rules: BrainSignalRule[];
  policy: BrainSignalPolicy;
  actions: BrainSignalActions;
  /** Absent on a rules-only definition. */
  semantic?: BrainSignalSemantic;
  createdAt: string;
  /** User id; sealed at rest. */
  createdBy: string;
}

export interface BrainSignalDefinition {
  teamId: string;
  defId: string;
  status: BrainSignalDefinitionStatus;
  activeVersion: number;
  templateId?: string;
  updatedAt: string;
  active: BrainSignalDefinitionVersion;
}

export enum BrainSignalClosedReason {
  RESOLVED = 'RESOLVED',
  DECAYED = 'DECAYED',
  DEFINITION_CHANGED = 'DEFINITION_CHANGED',
  SCOPE_MERGED = 'SCOPE_MERGED',
  ADMIN = 'ADMIN',
}

export interface BrainSignal {
  teamId: string;
  /** `${defId}#${entityId}` */
  signalId: string;
  defId: string;
  defVersion: number;
  scopeEntityId: string;
  scopeEntityType: BrainEntityType;
  level: BrainSignalLevel;
  score: number;
  status: BrainSignalStatus;
  version: number;
  openedAt: string;
  lastChangeAt: string;
  lastEvaluatedAt: string;
  /** Sealed at rest. */
  summary?: string;
  citedAssertionIds: string[];
  distinctSourceKeys: string[];
  areaKeys: string[];
  spaceId?: string;
  closedReason?: BrainSignalClosedReason;
}

export enum BrainSignalEngine {
  RULES = 'RULES',
  HAIKU = 'HAIKU',
  SONNET = 'SONNET',
  JEV = 'JEV',
}

export enum BrainSignalPolicyApplied {
  NONE = 'NONE',
  BASELINE = 'BASELINE',
  COOLDOWN = 'COOLDOWN',
  BUDGET_EXCEEDED = 'BUDGET_EXCEEDED',
  REVIEW_REQUIRED = 'REVIEW_REQUIRED',
}

export interface BrainSignalEventRef {
  eventId: string;
  kind: BrainEventKindAny;
  at: string;
}

export interface BrainSignalHistoryEntry {
  signalId: string;
  version: number;
  at: string;
  fromLevel: BrainSignalLevel;
  toLevel: BrainSignalLevel;
  fromStatus: BrainSignalStatus;
  toStatus: BrainSignalStatus;
  eventRefs: BrainSignalEventRef[];
  engine: BrainSignalEngine;
  model?: string;
  promptVersion?: string;
  defVersion: number;
  confidence?: number;
  /** Sealed at rest. */
  rationale?: string;
  reasonCodes: string[];
  escalationReasons?: BrainSignalEscalationReason[];
  inputAssertionIds: string[];
  citedAssertionIds: string[];
  newCitations: string[];
  costMicroUsd: number;
  policyApplied: BrainSignalPolicyApplied;
  actionKeys: string[];
}

export enum BrainSignalActionKind {
  NOTIFY_IN_APP = 'NOTIFY_IN_APP',
  NOTIFY_EMAIL = 'NOTIFY_EMAIL',
  FLOW_TRIGGER = 'FLOW_TRIGGER',
}

export enum BrainSignalActionState {
  CLAIMED = 'CLAIMED',
  SENT = 'SENT',
}

export interface BrainSignalAction {
  signalId: string;
  contentKey: string;
  kind: BrainSignalActionKind;
  state: BrainSignalActionState;
  at: string;
  recipientCount?: number;
  /** FLOW_TRIGGER rows only: the flow and key the stale retry re-sends, and the history version it describes. */
  agentId?: string;
  triggerKey?: string;
  version?: number;
}

/** An action a reviewBeforeAction transition held back; `recipients` are user ids, sealed at rest. */
export interface BrainSignalHeldAction {
  kind: BrainSignalActionKind;
  recipients?: string[];
  agentId?: string;
  triggerKey?: string;
}

export interface BrainSignalHeldPlan {
  held: BrainSignalHeldAction[];
  /** The DoBox confirmation card, once the worker has created it. */
  cardId?: string;
}

export const BRAIN_SIGNAL_LIMITS = {
  MAX_DEFINITIONS_PER_TEAM: 50,
  MAX_RULES_PER_DEFINITION: 8,
  MAX_CITED: 50,
  HISTORY_PAGE: 50,
  SCOPE_FACTS_WINDOW_DAYS: 90,
} as const;

export const BRAIN_SIGNAL_WINDOW = {
  DEBOUNCE_MINUTES: 2,
  MAX_ITERATIONS: 5,
} as const;

export const BRAIN_SIGNAL_SCORE_LEVELS: ReadonlyArray<{
  min: number;
  level: BrainSignalLevel;
}> = [
  { min: 0, level: BrainSignalLevel.NONE },
  { min: 1, level: BrainSignalLevel.LOW },
  { min: 2, level: BrainSignalLevel.MODERATE },
  { min: 3, level: BrainSignalLevel.HIGH },
  { min: 4, level: BrainSignalLevel.CRITICAL },
];

/** Highest band whose floor the score reaches; a negative score is NONE. */
export const brainSignalLevelForScore = (score: number): BrainSignalLevel =>
  BRAIN_SIGNAL_SCORE_LEVELS.reduce<BrainSignalLevel>(
    (level, band) => (score >= band.min ? band.level : level),
    BrainSignalLevel.NONE,
  );

export const brainSignalLevelRank = (level: BrainSignalLevel): number =>
  BRAIN_SIGNAL_LEVEL_ORDER.indexOf(level);
